import React, { useState, useEffect } from 'react';
import { Typography, Collapse, Breadcrumb, Spin, Table, Image, Grid } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { HomeOutlined, CaretRightOutlined } from '@ant-design/icons';
import './FAQ.less';
const { useBreakpoint } = Grid;
const { Title, Text } = Typography;
const { Panel } = Collapse;
import bannerFAQ from '@/assets/images/banner-faq.jpg';

interface TableData {
  columns: {
    title: string;
    dataIndex: string;
    key: string;
  }[];
  dataSource: Record<string, string>[];
  className?: string;
}

interface FAQItem {
  question: string;
  answer: string;
  table?: TableData;
}

// FAQ数据
const faqDataMap: { [key: string]: FAQItem[] } = {
  'mlcc': [
    {
      question: "电容器有哪些种类?",
      answer: "一般家用电器上使用的主要电容器的种类如下表所示。",
      table: {
        columns: [
          { title: '种类', dataIndex: 'parameter', key: 'parameter' },
          { title: '特征', dataIndex: 'description', key: 'description' },
          { title: '用途', dataIndex: 'range', key: 'range' }
        ],
        dataSource: [
          {
            key: '1',
            parameter: '多层陶瓷电容器（MLCC）',
            description: '超宽容量范围：0.5pF~100μF全覆盖，适应多样化场景。\n 高频性能优异：ESR/ESL极低，可无损传输模拟信号。\n 温度敏感缺陷：高介电常数型号（如X7R）容量随温度/电压变化显著。',
            range: '数字电路去耦（CPU/GPU供电）\n 高频滤波（射频模块、传感器）\n电源瞬态响应增强'
          },
          {
            key: '2',
            parameter: '铝电解电容器',
            description: '超大容量：静电容量可达μF至mF级，适合储能场景。\n 频率特性局限：ESR（等效串联电阻）和ESL（等效串联电感）较高，不适用于高频去耦合。\n 极性敏感：反向电压或过压易导致漏液、爆裂，需严格区分正负极。',
            range: '电源电路平滑滤波（如整流后稳压）\n低频备份电路（如断电保护）'
          },
          {
            key: '3',
            parameter: '钽电解电容器',
            description: '小型化设计：相同容量下体积比铝电解电容小50%以上。\n 性能提升：ESR和ESL低于铝电解，但仍逊于陶瓷电容。\n 安全风险：过压或反向电压可能引发冒烟、起火，需搭配保护电路。',
            range: '紧凑型设备电源平滑（如手机主板）\n桥式电路信号耦合'
          },
          {
            key: '4',
            parameter: '薄膜电容器',
            description: '稳定性卓越：容量几乎不受温度、电压波动影响。\n无极性设计：支持双向电压，简化电路布局。\n耐压性强：可承受数百至数千伏高压，损耗角低。',
            range: '模拟信号处理（如音频耦合、滤波）\n高频脉冲吸收（如开关电源缓冲）'
          }
        ]
      }
    },
    {
      question: "陶瓷电容器的主要规格参数有哪些？",
      answer: "以下是陶瓷电容器的主要规格参数表：",
      table: {
        columns: [
          { title: '参数', dataIndex: 'parameter', key: 'parameter' },
          { title: '说明', dataIndex: 'description', key: 'description' },
          { title: '典型值范围', dataIndex: 'range', key: 'range' }
        ],
        dataSource: [
          {
            key: '1',
            parameter: '额定电压',
            description: '电容器能够承受的最大工作电压',
            range: '6.3V ~ 3000V'
          },
          {
            key: '2',
            parameter: '容量',
            description: '电容器储存电荷的能力',
            range: '0.47pF ~ 100μF'
          },
          {
            key: '3',
            parameter: '温度特性',
            description: '在不同温度下容量的变化特性',
            range: 'C0G, X5R, X7R, Y5V等'
          },
          {
            key: '4',
            parameter: '尺寸规格',
            description: '电容器的物理尺寸',
            range: '01005, 0201, 0402, 0603, 0805等'
          }
        ]
      }
    },
    {
      question: "陶瓷电容器的温度特性对比",
      answer: "不同温度特性的陶瓷电容器具有不同的性能表现：",
      table: {
        columns: [
          { title: '温度特性', dataIndex: 'type', key: 'type' },
          { title: '温度范围', dataIndex: 'range', key: 'range' },
          { title: '容量变化', dataIndex: 'variation', key: 'variation' },
          { title: '推荐应用', dataIndex: 'application', key: 'application' }
        ],
        dataSource: [
          {
            key: '1',
            type: 'C0G (NP0)',
            range: '-55℃ ~ +125℃',
            variation: '±30 ppm/℃',
            application: '要求高精度的电路，如滤波、振荡'
          },
          {
            key: '2',
            type: 'X7R',
            range: '-55℃ ~ +125℃',
            variation: '±15%',
            application: '一般耦合、去耦合、旁路应用'
          },
          {
            key: '3',
            type: 'X5R',
            range: '-55℃ ~ +85℃',
            variation: '±15%',
            application: '一般用途、消费电子'
          },
          {
            key: '4',
            type: 'Y5V',
            range: '-30℃ ~ +85℃',
            variation: '+22%/-82%',
            application: '非关键、低成本应用'
          }
        ]
      }
    },
    {
      question: "陶瓷电容器的常见失效模式有哪些？",
      answer: "陶瓷电容器的主要失效模式包括：",
      table: {
        columns: [
          { title: '失效模式', dataIndex: 'mode', key: 'mode' },
          { title: '原因', dataIndex: 'cause', key: 'cause' },
          { title: '预防措施', dataIndex: 'prevention', key: 'prevention' }
        ],
        dataSource: [
          {
            key: '1',
            mode: '机械应力开裂',
            cause: 'PCB弯曲、热应力、机械冲击',
            prevention: '优化PCB布局，避免应力集中'
          },
          {
            key: '2',
            mode: '热应力失效',
            cause: '温度循环、焊接温度过高',
            prevention: '控制焊接温度，优化温度曲线'
          },
          {
            key: '3',
            mode: '电压击穿',
            cause: '过电压、电压尖峰',
            prevention: '选择合适的额定电压，添加保护电路'
          },
          {
            key: '4',
            mode: '温度特性漂移',
            cause: '温度变化超出规格范围',
            prevention: '选择合适温度特性的产品'
          }
        ]
      }
    },
    {
      question: "如何正确选择陶瓷电容器的尺寸？",
      answer: "选择陶瓷电容器尺寸时需要考虑以下因素：",
      table: {
        columns: [
          { title: '尺寸规格', dataIndex: 'size', key: 'size' },
          { title: '典型容量范围', dataIndex: 'capacity', key: 'capacity' },
          { title: '应用场景', dataIndex: 'application', key: 'application' }
        ],
        dataSource: [
          {
            key: '1',
            size: '01005 (0.4×0.2mm)',
            capacity: '0.1pF ~ 1μF',
            application: '超小型移动设备、可穿戴设备'
          },
          {
            key: '2',
            size: '0201 (0.6×0.3mm)',
            capacity: '0.1pF ~ 10μF',
            application: '智能手机、平板电脑'
          },
          {
            key: '3',
            size: '0402 (1.0×0.5mm)',
            capacity: '0.1pF ~ 22μF',
            application: '一般消费电子、通信设备'
          },
          {
            key: '4',
            size: '0603 (1.6×0.8mm)',
            capacity: '0.1pF ~ 47μF',
            application: '工业设备、汽车电子'
          },
          {
            key: '5',
            size: '0805 (2.0×1.25mm)',
            capacity: '0.1pF ~ 100μF',
            application: '大功率设备、电源系统'
          }
        ]
      }
    },
    {
      question: "陶瓷电容器的焊接注意事项有哪些？",
      answer: "陶瓷电容器焊接时需要注意以下几点：",
      table: {
        columns: [
          { title: '注意事项', dataIndex: 'point', key: 'point' },
          { title: '原因', dataIndex: 'reason', key: 'reason' },
          { title: '建议', dataIndex: 'suggestion', key: 'suggestion' }
        ],
        dataSource: [
          {
            key: '1',
            point: '温度控制',
            reason: '过高温度会导致内部应力',
            suggestion: '遵循推荐的焊接温度曲线'
          },
          {
            key: '2',
            point: '焊盘设计',
            reason: '不当的焊盘设计会导致应力集中',
            suggestion: '使用对称的焊盘设计，避免应力集中'
          },
          {
            key: '3',
            point: '焊接时间',
            reason: '过长焊接时间会导致热损伤',
            suggestion: '控制焊接时间在推荐范围内'
          },
          {
            key: '4',
            point: '冷却过程',
            reason: '快速冷却会产生热应力',
            suggestion: '自然冷却，避免强制冷却'
          }
        ]
      }
    }
  ],
  'aec-q200': [
    {
      question: "高可靠性元件的区分有哪些种类?",
      answer: "按应用领域分类",
      table: {
        columns: [
          { title: '应用领域', dataIndex: 'application', key: 'application' },
          { title: '规格代码', dataIndex: 'code', key: 'code' },
          { title: '品质等级', dataIndex: 'quality', key: 'quality' },
          { title: '适配场景', dataIndex: 'scene', key: 'scene' },
          { title: '典型元件', dataIndex: 'component', key: 'component' },
          { title: '认证标准', dataIndex: 'standard', key: 'standard' },
          { title: '关键技术指标', dataIndex: 'indicator', key: 'indicator', }
        ],
        dataSource: [
          {
            key: '1',
            application: '车载控制安全',
            code: 'A',
            quality: '1',
            scene: '动力总成/ADAS系统',
            component: '抗硫化贴片电阻、高温钽电容',
            standard: 'AEC-Q200 Rev H \nIATF 16949',
            indicator: '-55°C~150°C耐温\n50G机械冲击耐受'
          },
          {
            key: '2',
            application: '车载智能车身',
            code: 'C',
            quality: '2',
            scene: '信息娱乐/车身控制模块',
            component: 'CAN总线收发器、LED驱动IC',
            standard: 'AEC-Q100 Grade 2\nCISPR 25',
            indicator: '8KV ESD防护\n85°C持续运行寿命≥5年'
          },
          {
            key: '3',
            application: '工业通信设备',
            code: 'B',
            quality: '2',
            scene: '5G基站/工业自动化终端',
            component: '工业级POE模块、宽温电解电容',
            standard: 'IEC 60068-2-6（振动）\nIP67',
            indicator: '96h盐雾测试通过\nMTBF≥200,000小时'
          },
          {
            key: '4',
            application: '医疗高风险',
            code: 'M',
            quality: '2',
            scene: '生命支持/影像诊断设备',
            component: '医疗级电容、高可靠性电阻',
            standard: 'ISO 13485\nIEC 60601-1 Ed.4',
            indicator: '生物相容性（ISO 10993）\n伽马射线灭菌'
          },
          {
            key: '5',
            application: '医疗中低风险',
            code: 'L',
            quality: '3',
            scene: '家用健康监测设备',
            component: '便携式传感器、蓝牙MCU',
            standard: 'GHTF Class I\nRoHS 3',
            indicator: '0.1μA超低待机功耗\n±1%测量精度'
          }
        ],
        className: 'table-preline'
      }
    },
    {
      question: "什么是AEC-Q200?",
      answer: "AEC-Q200是汽车电子协会AEC为“无源元器件”制定的车规级标准规则， 设置了高温高湿、耐热、冲击、耐久性等支持高可靠性的规格。"
    }
  ],
  'inductor': [
    {
      question: "电感器的分类有哪些？",
      answer: "EETL代理的电感器分类与应用指南如下：",
      table: {
        columns: [
          { title: '分类维度', dataIndex: 'category', key: 'category' },
          { title: '系列名称/型号', dataIndex: 'series', key: 'series' },
          { title: '结构特点', dataIndex: 'structure', key: 'structure' },
          { title: '关键参数', dataIndex: 'parameters', key: 'parameters' },
          { title: '典型应用场景', dataIndex: 'application', key: 'application' },
          { title: '产品优势', dataIndex: 'advantages', key: 'advantages' }
        ],
        dataSource: [
          {
            key: '1',
            category: '功率电感',
            series: 'NR系列',
            structure: '绕线式磁屏蔽结构',
            parameters: '电感值：1μH~100μH\n额定电流：0.3A~10A',
            application: '电源模块DC-DC转换器\n车载ECU供电',
            advantages: '低损耗铁氧体磁芯\n抗电磁干扰强'
          },
          {
            key: '2',
            series: 'CBC系列',
            structure: '扁平线圈复合磁性材料',
            parameters: '电感值：0.1μH~47μH\n饱和电流：3A~25A',
            application: '大电流降压电路\nCPU/GPU内核供电',
            advantages: '超低DCR（<10mΩ）\n耐高温125℃'
          },
          {
            key: '3',
            category: '高频电感',
            series: 'MA系列',
            structure: '多层陶瓷叠层工艺',
            parameters: '频率范围：10MHz~6GHz\nQ值：30~100',
            application: '射频模块匹配电路\n5G通信滤波器',
            advantages: '高Q值低ESR\n微型化封装（0402~1005）'
          },
          {
            key: '4',
            category: '',
            series: 'HK系列',
            structure: '薄膜工艺精密线圈',
            parameters: '电感公差：±2%\n温度系数：±50ppm/℃',
            application: '高频振荡电路\n卫星通信设备',
            advantages: '超高频率稳定性\nAEC-Q200认证'
          },
          {
            key: '5',
            category: '汽车级电感',
            series: 'AEC-Q系列',
            structure: '增强型封装与材料',
            parameters: '工作温度：-55℃~+150℃\n耐振动50G',
            application: '新能源汽车OBC\nADAS传感器供电',
            advantages: '通过AEC-Q200认证\n抗硫化设计'
          },
          {
            key: '6',
            category: '抗噪声电感',
            series: 'NF系列',
            structure: '共模扼流圈结构',
            parameters: '阻抗：10Ω~1kΩ@100MHz\n额定电压：80V',
            application: 'USB/HDMI信号滤波\nCAN总线噪声抑制',
            advantages: '共模差模双抑制\n低插入损耗'
          },
          {
            key: '7',
            category: '超薄电感',
            series: 'CLF系列',
            structure: '扁平化绕线设计',
            parameters: '厚度：0.8mm~1.2mm\n电流：0.5A~3A',
            application: '智能手机主板\nTWS耳机充电仓',
            advantages: '节省空间设计\n支持回流焊工艺'
          }
        ],
        className: 'table-preline'
      }
    },
    {
      question: "电感器的主要参数有哪些？",
      answer: "电感器的主要参数包括：1. 电感值（单位：亨利H）；2. 额定电流；3. 直流电阻（DCR）；4. 品质因数（Q值）；5. 自谐振频率（SRF）。"
    },
    
  ]
};

const FAQ: React.FC = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [faqData, setFaqData] = useState<FAQItem[]>([]);

  const screens = useBreakpoint();

  const handleFAQClick = () => {
    navigate('/');
    // 使用setTimeout确保在页面加载后执行滚动
    setTimeout(() => {
      const faqElement = document.getElementById('FAQ');
      if (faqElement) {
        faqElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  useEffect(() => {
    // 模拟加载效果
    setLoading(true);
    setTimeout(() => {
      setFaqData(faqDataMap[category || ''] || []);
      setLoading(false);
    }, 500);
  }, [category]);

  const getCategoryTitle = (category: string) => {
    const categoryMap: { [key: string]: string } = {
      'mlcc': '多层陶瓷电容器专区',
      'aec-q200': '高可靠性元件',
      'inductor': '电感器专区',
    };
    return categoryMap[category] || '常见问题';
  };

  const renderAnswer = (item: FAQItem) => {
    return (
      <div className="faq-answer-container">
        <Text className="faq-answer">{item.answer}</Text>
        {item.table && (
          <div className="faq-table">
            <Table
              columns={item.table.columns}
              dataSource={item.table.dataSource}
              pagination={false}
              className={`tech-table ${item.table.className || ''}`}
              size="middle"
              bordered
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="" style={{paddingBottom: '80px'}}>
      <div className='' style={{maxWidth: '1500px',margin: '0 auto'}}>
        <Image
          width={'100%'}
          preview={false}
          src={bannerFAQ}
          style={{ height: screens.md ? '250px' : '150px', objectFit: 'cover', objectPosition: '36% 50%' }}
        /> 
        
        <div className='' style={{background: '#f5f5f5'}}>
          <div className='container'>
              <Breadcrumb
                  items={[
                      {
                      href: '/',
                      title: <HomeOutlined />,
                      },
                      {
                      title: <span onClick={handleFAQClick} style={{ cursor: 'pointer' }}>Q&A专区</span>,
                      },
                      {
                      title: getCategoryTitle(category || ''),
                      },
                  ]}
                  className="breadcrumbs"
              />
          </div>
        </div>
      </div>
      <div className="container">
        <div className="faq-content">
            <Title level={2} className="text-center text-primary mt-30 mb-30">
            {getCategoryTitle(category || '')}
            </Title>

            {loading ? (
            <div className="faq-loading">
                <Spin size="large" />
            </div>
            ) : (
                <div>
                    {faqData.length > 0 ? (
                    <Collapse
                        className="faq-collapse"
                        defaultActiveKey={['0']}
                        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                    >
                        {faqData.map((item, index) => (
                            
                            <Panel
                                header={
                                <Text strong className="faq-question">
                                    {item.question}
                                </Text>
                                }
                                key={index}
                            >
                                {renderAnswer(item)}
                            </Panel>
                        ))}
                    </Collapse>
                    ) : (
                    <div className="faq-empty">
                        <Text>暂无相关问题</Text>
                    </div>
                    )}
                </div>
            )}
        </div>

        
      </div>
    </div>
  );
};

export default FAQ;
